import { useMemo, useCallback } from 'react';
import {
  format,
  parseISO,
  startOfMonth,
  startOfISOWeek,
  endOfISOWeek,
  getISOWeek,
  isWithinInterval,
} from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Workorder } from '@certhon/models';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { usePanelState } from '@/app/navigation/usePanelState';
import type { MyWorkordersView } from '@/features/user-preferences/user-preferences.hooks';
import { cn } from '@/lib/utils';
import { WorkorderCard } from '../shared';
import { UserSwitcher } from '../myday/components/UserSwitcher';
import { WorkorderNavigation } from './components/WorkorderNavigation';
import { ViewModeToggle } from './components/ViewModeToggle';
import { DayGroupHeader } from './components/DayGroup';
import { DayGroup, groupByDay } from './components/dayGroups';
import { useMyMonthWorkorders } from './myweek.hooks';

interface WeekGroup {
  /** yyyy-MM-dd of the ISO Monday. 'unscheduled' for workorders without execution_date. */
  key: string;
  weekStart: Date | null;
  days: DayGroup[];
  count: number;
  /** Every day in this week lies before the viewed month. */
  isCarryOver: boolean;
}

/**
 * Group workorders per ISO week, each week split per day. Weeks that lie
 * entirely before `monthStart` are carry-over; a week straddling the month
 * boundary is shown normally with its individual days flagged.
 */
function groupByWeek(workorders: Workorder[], monthStart: Date): WeekGroup[] {
  const map = new Map<string, WeekGroup>();

  for (const day of groupByDay(workorders, monthStart)) {
    const weekStart = day.date ? startOfISOWeek(day.date) : null;
    const key = weekStart ? format(weekStart, 'yyyy-MM-dd') : 'unscheduled';

    if (!map.has(key)) {
      map.set(key, {
        key,
        weekStart,
        days: [],
        count: 0,
        isCarryOver: true,
      });
    }
    const week = map.get(key)!;
    week.days.push(day);
    week.count += day.workorders.length;
    week.isCarryOver &&= day.isCarryOver;
  }

  // groupByDay already yields days in order, so weeks are inserted in order.
  return Array.from(map.values());
}

interface MyMonthPanelProps extends RoutedPanelProps {
  mode: MyWorkordersView;
  onModeChange: (mode: MyWorkordersView) => void;
}

export const MyMonthPanel = ({
  params,
  path,
  mode,
  onModeChange,
  ...panelProps
}: MyMonthPanelProps) => {
  const dateParam = params.date || format(new Date(), 'yyyy-MM-dd');
  const monthStart = useMemo(
    () => startOfMonth(parseISO(dateParam)),
    [dateParam],
  );
  const monthStartIso = format(monthStart, 'yyyy-MM-dd');

  const [panelState, setPanelState] = usePanelState<{ userId?: string }>();
  const userId = panelState.userId ? parseInt(panelState.userId, 10) : null;

  const { data: workorders, isLoading } = useMyMonthWorkorders(
    userId,
    monthStartIso,
  );

  const weeks = useMemo(
    () => groupByWeek(workorders ?? [], monthStart),
    [workorders, monthStart],
  );

  const handleUserChange = useCallback(
    (newUserId: number | null) => {
      setPanelState({ userId: newUserId?.toString() });
    },
    [setPanelState],
  );

  const today = new Date();

  return (
    <Panel {...panelProps} title="My Workorders">
      <div className="bg-card sticky top-[41px] z-[5] space-y-2 pb-2 md:top-[49px]">
        <WorkorderNavigation date={dateParam} mode={mode} />
        <div className="flex items-center justify-between gap-2">
          <UserSwitcher
            selectedUserId={userId}
            onUserChange={handleUserChange}
          />
          <ViewModeToggle mode={mode} onChange={onModeChange} />
        </div>
      </div>

      {isLoading ? (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-live="polite"
        >
          <Loader2
            className="text-muted-foreground h-8 w-8 animate-spin"
            aria-hidden="true"
          />
          <span className="sr-only">Loading workorders...</span>
        </div>
      ) : weeks.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          No workorders for this month
        </div>
      ) : (
        <div className="space-y-8 pb-4">
          {weeks.map((week) => (
            <section key={week.key} className="space-y-3">
              <WeekGroupHeader week={week} today={today} />
              {week.days.map((day) => (
                <div key={day.key} className="space-y-2 pl-2">
                  <DayGroupHeader group={day} today={today} showWeek={false} />
                  <div className="space-y-2 sm:space-y-1">
                    {day.workorders.map((wo, idx) => (
                      <WorkorderCard
                        key={wo.recnum}
                        mode="readonly"
                        workorder={wo}
                        index={idx}
                        linkBase={path}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
};

function WeekGroupHeader({ week, today }: { week: WeekGroup; today: Date }) {
  const countLabel = `${week.count} ${week.count === 1 ? 'workorder' : 'workorders'}`;

  if (!week.weekStart) {
    return (
      <div className="flex items-baseline gap-2 border-b pb-1 text-base font-semibold">
        <span>Unscheduled</span>
        <span className="text-muted-foreground text-xs font-normal">
          {countLabel}
        </span>
      </div>
    );
  }

  const weekEnd = endOfISOWeek(week.weekStart);
  const isCurrentWeek = isWithinInterval(today, {
    start: week.weekStart,
    end: weekEnd,
  });

  return (
    <div
      className={cn(
        'flex items-baseline gap-2 border-b pb-1 text-base font-semibold',
        week.isCarryOver && 'text-amber-700 dark:text-amber-300',
        isCurrentWeek && !week.isCarryOver && 'border-primary',
      )}
    >
      <span>Week {getISOWeek(week.weekStart)}</span>
      <span className="text-muted-foreground text-xs font-normal">
        {format(week.weekStart, 'd MMM')} – {format(weekEnd, 'd MMM')} ·{' '}
        {countLabel}
      </span>
      {week.isCarryOver && (
        <span
          className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800
            dark:bg-amber-950/40 dark:text-amber-300"
        >
          Carry-over
        </span>
      )}
      {isCurrentWeek && !week.isCarryOver && (
        <span className="text-primary ml-1 text-xs font-normal">This week</span>
      )}
    </div>
  );
}
